// FAR-75: the Planning Board's shell -- its tabs and its two header pills.
// Pure, so the rules are tested without a browser.

// The planning horizons. FAR-77 §4 / FAR-83 §0: Phase 1 has two tabs; the
// Weekly tab is removed (not shown, not even locked). Labels here are the
// fallback until the plan's months load ("Oct 2026 → Dec 2026").
export const TABS = [
  { key: '3month', label: 'Next 3 Months', locked: false },
  { key: '4-12month', label: 'Months 4–18', locked: false },
  // FAR-89 #1: the Base Plan on its own, all 18 months, nothing editable.
  { key: 'baseplan', label: 'Base Plan', locked: false },
]

// FAR-89 #1: the Base Plan tab shows the whole 18-month horizon.
export const BASE_PLAN_MONTHS = 18
export function basePlanTabMonths(months) {
  return (months || []).slice(0, BASE_PLAN_MONTHS)
}

// The Base Plan tab's gross hours (line -> yyyy-mm -> hours) are the two other
// tabs' GET /capacity/lines results put together: months 0–3 and months 4–18.
// Each tab's own months win; no month is in both.
export function mergeGrossHours(...parts) {
  const out = {}
  for (const part of parts) {
    for (const [line, byMonth] of Object.entries(part || {})) {
      out[line] = { ...(out[line] || {}), ...(byMonth || {}) }
    }
  }
  return out
}

// FAR-75 §3: "Phase 1 opens on 4–18 Month (confirm)" -- Open Item 3.
export const DEFAULT_TAB = '4-12month'

// resolveTab picks the tab to show for a requested key (e.g. from the URL).
// Unknown or locked tabs fall back to the default, so a stale link (an old
// "?tab=weekly") can never open a tab that isn't available.
export function resolveTab(requested) {
  const tab = TABS.find((t) => t.key === requested)
  return tab && !tab.locked ? tab.key : DEFAULT_TAB
}

// FAR-88 S2 / FAR-90 S2: a timestamp reads "Sep 22, 2026 · 3:14 PM" -- the
// date, a middle dot, then the time (not "Sep 22, 2026, 3:14 PM"). Local time.
// null for a missing or unparseable value, so callers never show Invalid Date.
export function formatStamp(value) {
  if (value === null || value === undefined || value === '') return null
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return null
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return `${date} · ${time}`
}

// FAR-75 §2: the Base Plan pill shows the Base Plan's name, which is its
// system-assigned creation timestamp. FAR-88 S2: the label ("Base Plan:") is
// muted and the value bold, so the pill takes the two parts separately.
export function basePlanPill(basePlan, hasBasePlanId) {
  if (!hasBasePlanId) return { label: 'Base Plan:', value: 'none' }
  return { label: 'Base Plan:', value: formatStamp(basePlan?.createdAt) || '…' }
}

export function basePlanPillLabel(basePlan, hasBasePlanId) {
  const { label, value } = basePlanPill(basePlan, hasBasePlanId)
  return `${label} ${value}`
}

// FAR-75 §2: the Modification pill shows the plan's Modification list name.
// A plan created without a list has none yet; it still opens the panel, where
// modifications can be added (they auto-save).
export function modificationPill(listName, hasListId) {
  if (!hasListId) return { label: 'Modification:', value: 'none' }
  return { label: 'Modification:', value: listName || '…' }
}

export function modificationPillLabel(listName, hasListId) {
  const { label, value } = modificationPill(listName, hasListId)
  return `${label} ${value}`
}

// FAR-88 H2 / S3, FAR-77 §4: the release pill, "Released through Dec 2026 ·
// Jan releasing next". Information only (FAR-90 D2: the window is the Base
// Plan's horizon; releasing a month never moves it).
//
// No release record exists in the system yet (no API, table or field holds a
// release position), so by default the pill is derived: Phase 1 treats the
// Next 3 Months window as the released months, so "released through" is that
// window's last month and the month after it is "releasing next" -- the
// mockup's reading for an Oct–Dec window. When a release record exists, pass
// its month as `releasedThrough` ("YYYY-MM") and it wins.
// null until the months load, so nothing is shown rather than a guess.
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export function releasePill(nearMonths, releasedThrough) {
  const iso = releasedThrough || (nearMonths?.length ? nearMonths[nearMonths.length - 1]?.iso : null)
  const m = /^(\d{4})-(\d{2})/.exec(String(iso || ''))
  if (!m) return null
  const year = Number(m[1])
  const month = Number(m[2]) // 1–12
  if (month < 1 || month > 12) return null
  return {
    through: `Released through ${SHORT_MONTHS[month - 1]} ${year}`,
    next: `${SHORT_MONTHS[month % 12]} releasing next`,
  }
}

// FAR-89 §2: the breadcrumb's "Plans" link returns to Plan Selection with
// this plan's folder selected. No folder (still loading, or none): plain
// /plans, which opens on the newest folder.
export function plansHref(folderId) {
  return folderId ? `/plans?folder=${encodeURIComponent(folderId)}` : '/plans'
}

// FAR-75 §2: subtitle "Folder / Production Plan name".
export function boardSubtitle(folderName, planName) {
  return [folderName, planName].filter(Boolean).join(' / ')
}
